<template>
  <div class="h-full overflow-y-auto relative [&>.sticky]:pt-4">
    <div class="absolute inset-0 px-4 flex flex-col">
      <div class="sticky top-0 z-10 bg-surface-0 dark:bg-surface-800 -mr-px">
        <p
          class="text-sm font-medium ma-text-secondary"
          :class="small ? 'my-4 font-bold' : 'mt-8 mb-2'"
        >
          ATTACHMENTS
        </p>

        <!-- Media limit info component -->
        <MediaLimitInfo
          :image-limit-reached="hasReachedImageLimit"
          :video-limit-reached="hasReachedVideoLimit"
          :max-images="maxImages"
          :max-videos="maxVideos"
          :platform="platform"
          class="mb-2"
        />

        <AppTabs
          :items="tabItems"
          option-label="label"
          option-value="value"
          :selected-item="selectedTab"
          @selected="selectedTab = $event.value"
        />
      </div>
      <div class="grow">
        <ContentEditUploadMedia
          v-if="selectedTab === 'upload'"
          :max-image-size="maxFileSize"
          :max-video-size="200 * 1024 * 1024"
          :small="small"
          :platform="supportedPlatform"
          :disable-image-selection="hasReachedImageLimit"
          :disable-video-selection="hasReachedVideoLimit"
          @image-selected="emit('imageSelected', $event)"
          @video-selected="emit('videoSelected', $event)"
        />
        <ContentEditGenerateImage
          v-else-if="selectedTab === 'generate'"
          :images="imageMap"
          :small="small"
          :disabled="hasReachedImageLimit"
          @done="emit('imageSelected', $event)"
        />

        <ContentEditImageUrl
          v-else-if="selectedTab === 'url'"
          :images="imageMap"
          :videos="videoMap"
          :small="small"
          :disable-image-selection="hasReachedImageLimit"
          :disable-video-selection="hasReachedVideoLimit"
          @image-selected="emit('imageSelected', $event)"
          @video-selected="emit('videoSelected', $event)"
        />
        <ContentEditImageLibrary
          v-else
          :images="imageMap"
          :videos="videoMap"
          :disable-image-selection="hasReachedImageLimit"
          :disable-video-selection="hasReachedVideoLimit"
          @image-selected="emit('imageSelected', $event)"
          @video-selected="emit('videoSelected', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AppTabs from "@/components/shared/AppTabs.vue";
import { useI18n } from "vue3-i18n";
import ContentEditGenerateImage from "./attachments/ContentEditGenerateImage.vue";
import ContentEditImageLibrary from "./attachments/ContentEditImageLibrary.vue";
import ContentEditImageUrl from "./attachments/ContentEditImageUrl.vue";
import ContentEditUploadMedia from "./ContentEditUploadMedia.vue";
import MediaLimitInfo from "./MediaLimitInfo.vue";
import { platformImageSpecs, type SupportedPlatform } from "@/utils/platformImageSpecs";

const props = defineProps<{
  images?: string[];
  videos?: string[];
  maxFileSize?: number;
  maxImages?: number;
  small?: boolean;
  maxVideos?: number;
  currentImageCount?: number;
  currentVideoCount?: number;
  platform?: string;
}>();

const emit = defineEmits<{
  (name: "imageSelected", payload: string): void;
  (name: "videoSelected", payload: string): void;
}>();

const { t } = useI18n();
const selectedTab = ref("library");

const supportedPlatform = computed<SupportedPlatform | undefined>(() =>
  props.platform && props.platform in platformImageSpecs
    ? (props.platform as SupportedPlatform)
    : undefined
);

const hasReachedImageLimit = computed(() => {
  return props.maxImages ? (props.currentImageCount || 0) >= props.maxImages : false;
});

const hasReachedVideoLimit = computed(() => {
  return props.maxVideos ? (props.currentVideoCount || 0) >= props.maxVideos : false;
});

const imageMap = computed(() => {
  if (!props.images) return {};
  return (
    props.images.reduce(
      (acc, image) => {
        acc[image] = true;
        return acc;
      },
      {} as Record<string, boolean>
    ) ?? {}
  );
});

const videoMap = computed(() => {
  if (!props.videos) return {};
  return (
    props.videos.reduce(
      (acc, video) => {
        acc[video] = true;
        return acc;
      },
      {} as Record<string, boolean>
    ) ?? {}
  );
});

const tabItems = computed(() => {
  return [
    {
      label: t("library"),
      value: "library",
    },
    {
      label: t("generate"),
      value: "generate",
    },
    {
      label: t("uploadMedia"),
      value: "upload",
    },
    {
      label: t("formsUrl").toUpperCase(),
      value: "url",
    },
  ];
});
</script>

<style scoped></style>
